import { useState, type FormEvent } from 'react';
import { z } from 'zod';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useInView } from '../hooks/useInView';

const demoFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
  company: z.string().min(1, 'Company is required').max(200),
  useCase: z.string().max(2000).optional(),
});

type DemoFormData = z.infer<typeof demoFormSchema>;
type FieldErrors = Partial<Record<keyof DemoFormData, string>>;

export function DemoForm() {
  const [ref, isInView] = useInView();
  const [formData, setFormData] = useState<DemoFormData>({
    name: '',
    email: '',
    company: '',
    useCase: '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitted, setSubmitted] = useState(false);

  function handleChange(field: keyof DemoFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const result = demoFormSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof DemoFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitted(true);
  }

  return (
    <section
      id="demo"
      className="relative py-24 sm:py-32 bg-gray-950"
      aria-labelledby="demo-heading"
    >
      <div
        className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`max-w-2xl mx-auto transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-center mb-12">
            <h2 id="demo-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              See Opair <span className="gradient-text">in Action</span>
            </h2>
            <p className="mt-4 text-lg text-gray-400">
              Request a personalized demo and see how Opair can govern AI usage across your
              organization.
            </p>
          </div>

          <div className="gradient-border p-8">
            {submitted ? (
              <div className="text-center py-12" role="alert">
                <CheckCircle
                  className="h-16 w-16 text-emerald-400 mx-auto mb-4"
                  aria-hidden="true"
                />
                <h3 className="text-2xl font-bold text-white">Thank you!</h3>
                <p className="mt-2 text-gray-400">
                  We&apos;ve received your request. Our team will reach out within 24 hours to
                  schedule your personalized demo.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate aria-label="Demo request form">
                <div className="space-y-5">
                  <FormField
                    id="demo-name"
                    label="Name"
                    value={formData.name}
                    error={errors.name}
                    onChange={(v) => handleChange('name', v)}
                    type="text"
                    placeholder="Jane Smith"
                    required
                  />
                  <FormField
                    id="demo-email"
                    label="Work Email"
                    value={formData.email}
                    error={errors.email}
                    onChange={(v) => handleChange('email', v)}
                    type="email"
                    placeholder="jane@company.com"
                    required
                  />
                  <FormField
                    id="demo-company"
                    label="Company"
                    value={formData.company}
                    error={errors.company}
                    onChange={(v) => handleChange('company', v)}
                    type="text"
                    placeholder="Acme Corp"
                    required
                  />
                  <div>
                    <label
                      htmlFor="demo-usecase"
                      className="block text-sm font-medium text-gray-300 mb-1.5"
                    >
                      Use Case <span className="text-gray-600">(optional)</span>
                    </label>
                    <textarea
                      id="demo-usecase"
                      rows={4}
                      value={formData.useCase}
                      onChange={(e) => handleChange('useCase', e.target.value)}
                      placeholder="Tell us about your AI governance challenges..."
                      className="w-full rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white hover:bg-indigo-500 transition-all hover:shadow-lg hover:shadow-indigo-500/20 focus-visible:ring-2 focus-visible:ring-indigo-400"
                  >
                    <Send className="h-4 w-4" aria-hidden="true" />
                    Request Demo
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FormField({
  id,
  label,
  value,
  error,
  onChange,
  type,
  placeholder,
  required,
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  type: string;
  placeholder: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        {required && (
          <span className="text-red-400 ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`w-full rounded-lg border px-4 py-2.5 text-sm text-white placeholder-gray-500 bg-gray-800/50 focus:ring-1 transition-colors ${
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-700 focus:border-indigo-500 focus:ring-indigo-500'
        }`}
      />
      {error && (
        <p
          id={`${id}-error`}
          className="mt-1.5 flex items-center gap-1 text-xs text-red-400"
          role="alert"
        >
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
